import styled from '@emotion/styled';
import { searchAccountListByIds } from 'src_server/types/accountData';
import { TablePropsType, TableWrapperTemplate, TableHeaderRowTemplate, TableRowTemplate, ColumnHeaderTemplate, TableCellTemplate, ChevronDownType, ChevronDownWrapperTemplate, TableSelectCellClickableTemplate, SectionMessageTemplate } from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import { fraudAndComplianceColors, statusLabel } from 'src/assets/colors';
import { ChevronDownIcon } from 'src/assets/icons/ChevronDownIcon';
import type { StylesConfig } from 'react-select';

export const TableWrapper = styled(TableWrapperTemplate, { label: 'TableWrapper' })`
    table-layout: fixed;
    width: 100%;
`;

export const TableHeaderRow = styled(TableHeaderRowTemplate, { label: 'TableHeaderRow' })``;

export const TableRow = styled(TableRowTemplate, { label: 'TableRow' })<TablePropsType>``;

export const ColumnHeader = styled(ColumnHeaderTemplate, { label: 'ColumnHeader' })`
    &:nth-of-type(1) {
        width: 35px;
        min-width: 35px;
        max-width: 35px;
    }
    &:nth-of-type(2) {
        width: 15%;
    }
    &:nth-of-type(3) {
        width: 14%;
    }
    &:nth-of-type(4) {
        width: 10%;
    }
    &:nth-of-type(5) {
        width: 10%;
    }
    &:nth-of-type(6) {
        width: 25%;
    }
    &:nth-of-type(7) {
        width: auto;
    }
`;

export const SortableColumnHeader = styled(ColumnHeader, { label: 'SortableColumnHeader' })`
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: ${fraudAndComplianceColors.commonTable.bgColorMessage};
    }
`;

export const TableCell = styled(TableCellTemplate, { label: 'TableCell' })`
    span {
        display: block;
    }
    &:nth-of-type(1) {
        width: 15%;
    }
    &:nth-of-type(2) {
        width: 15%;
    }
    &:nth-of-type(3) {
        width: 14%;
    }
    &:nth-of-type(4) {
        width: 10%;
    }
    &:nth-of-type(5) {
        width: 10%;
    }
    &:nth-of-type(6) {
        width: auto;
    }
`;

export const SectionMessage = styled(SectionMessageTemplate, { label: 'SectionMessage' })``;

export const ChevronDownWrapper = styled(ChevronDownWrapperTemplate, { label: 'ChevronDownWrapper' })<ChevronDownType>``;

export const TableSelectCellClickable = styled(TableSelectCellClickableTemplate, { label: 'TableSelectCellClickable' })`
    width: 35px;
`;

export const RowWithTable = styled('td', { label: 'RowWithTable' })`
    padding: 0 !important;
`;

export const InnerTable = styled('table', { label: 'InnerTable' })`
    table-layout: fixed;
    width: 100%;
    vertical-align: middle;
`;

export const TableRowAddColor = styled('tr', { label: 'TableRowAddColor' })`
    background: inherit !important;
`;

export const FilterWrapper = styled('div', { label: 'FilterWrapper' })`
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
`;

export const FilterLabel = styled('label', { label: 'FilterLabel' })`
    margin-right: 8px;
    font-size: 12px;
    color: ${fraudAndComplianceColors.commonTable.txtColor};
`;

export const FilterDropdownWrapper = styled('div', { label: 'FilterDropdownWrapper' })`
    min-width: 140px;
`;

export const FilterChevronIcon = styled(ChevronDownIcon, { label: 'FilterChevronIcon' })`
    fill: ${fraudAndComplianceColors.commonTable.txtColor};
    width: 10px;
`;

export const getFilterDropdownStyles = <T, IsMulti extends boolean>(): StylesConfig<T, IsMulti> => ({
    control: (base) => ({
        ...base,
        minHeight: '32px',
        height: '32px',
        border: `1px solid ${fraudAndComplianceColors.commonTable.borderColorDefault}`,
        borderRadius: '4px',
        backgroundColor: fraudAndComplianceColors.commonTable.bgColor,
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            borderColor: fraudAndComplianceColors.commonTable.borderColorDefault,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        height: '32px',
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
    }),
    singleValue: (base) => ({
        ...base,
        position: 'static',
        transform: 'none',
        margin: 0,
        fontSize: '12px',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        position: 'static',
        transform: 'none',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        paddingRight: '8px',
    }),
});

type KycStatusType = searchAccountListByIds.SuccessResponseType['results'][number]['properties']['kycStatus'];

interface KycStatusBadgeType {
    status: KycStatusType | '-' | string;
}

export const KycStatusBadge = styled('span', { label: 'KycStatusBadge' })<KycStatusBadgeType>`
    padding: 8px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${({ status }): string => {
        const s = status?.toLowerCase();
        if (s === 'passed') return statusLabel.live.bgColor;
        if (s === 'failed') return statusLabel.ended.bgColor;
        return statusLabel.scheduled.bgColor;
    }};
`;


export const ExpandedDetailsWrapper = styled('div', { label: 'ExpandedDetailsWrapper' })`
    padding: 12px;
    background-color: ${fraudAndComplianceColors.commonTable.bgColorMessage};
    border-radius: 4px;
`;

export const ExpandedDetailsTitle = styled('strong', { label: 'ExpandedDetailsTitle' })`
    font-size: 12px;
    color: ${fraudAndComplianceColors.commonTable.txtColor};
`;

export const JsonPreview = styled('pre', { label: 'JsonPreview' })`
    margin: 8px 0 0 0;
    padding: 8px;
    background-color: ${fraudAndComplianceColors.commonTable.bgColor};
    border: 1px solid ${fraudAndComplianceColors.commonTable.borderColorDefault};
    border-radius: 4px;
    font-size: 11px;
    overflow: auto;
    max-height: 300px;
`;

export const SmallText = styled('small', { label: 'SmallText' })`
    font-size: 10px;
    color: ${fraudAndComplianceColors.commonTable.txtColor};
    opacity: 0.7;
`;
